// Runtime branding configuration
interface BrandingConfig {
  appName: string;
  appTitle: string;
  headerTitle: string;
  pageTitle: string;
  logoPath: string;
  faviconPath: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  description: string;
  contractAssistantTitle: string;
  contractAssistantDescription: string;
  defaultAssistantTitle: string;
  defaultAssistantDescription: string;
}

/** Default tenant logo under `public/` (white-background Cortex horizontal lockup). */
export const DEFAULT_TENANT_LOGO_PATH = '/assets/cortex-logo-horizontal.svg';

/** Dark-background Cortex horizontal lockup for boot splash. */
export const CORTEX_SPLASH_LOGO_PATH = '/assets/cortex-logo-horizontal-inverted.svg';

/** Header icon for this package (UI-PARITY-SPEC section 10.6). */
export const STATIC_HEADER_ICON_PATH = '/assets/favicon.png';

/** Login and empty-state logo (UI-PARITY-SPEC section 10.6). */
export const STATIC_LOGO_PATH = '/assets/cortex-logo-horizontal.svg';

/** True when the path is a same-origin static asset, never an authenticated API URL. */
export function isStaticAssetPath(path: string): boolean {
  return Boolean(path) && path.startsWith('/') && !path.startsWith('/api/');
}

/**
 * Image src for the anonymous login page. Static /assets paths pass through.
 * Tenant-uploaded blob paths cannot be fetched without a token, so the default
 * logo is used. Never returns /api/branding (UI-PARITY-SPEC section 10.4).
 */
export const publicBrandingImageSrc = (blobPath?: string): string => {
  if (!blobPath) return DEFAULT_TENANT_LOGO_PATH;
  if (isStaticAssetPath(blobPath)) return blobPath;
  return DEFAULT_TENANT_LOGO_PATH;
};

/**
 * Convert a stored branding path to an <img> src.
 * Static /assets paths pass through. Blob-store paths return empty so the
 * theme provider can supply a blob URL from GET /api/cortex/console/branding/logo
 * or /favicon. Never returns /api/branding (UI-PARITY-SPEC section 10.4).
 */
export const getBrandingFileUrl = (blobPath: string): string => {
  if (!blobPath) return '';

  if (isStaticAssetPath(blobPath)) {
    return blobPath;
  }

  return '';
};

// COMMENTED OUT: This was causing the "Using fallback branding config" regression
// The tenant config system now handles all branding through TenantConfigContext
// and TenantThemeProvider, so this fallback is no longer needed.

/*
// Update document title when branding config is available
const updateDocumentTitle = (config: BrandingConfig) => {
  if (typeof document !== 'undefined') {
    document.title = config.pageTitle || config.appTitle || 'Cordexa AI';
  }
};

// Update favicon when branding config is available
const updateFavicon = (config: BrandingConfig) => {
  if (typeof document !== 'undefined' && config.faviconPath) {
    // Remove existing favicon links
    const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
    existingFavicons.forEach(link => link.remove());

    // Add new favicon link
    const faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.href = config.faviconPath;

    // Determine favicon type based on file extension
    const extension = config.faviconPath.split('.').pop()?.toLowerCase();
    if (extension === 'ico') {
      faviconLink.type = 'image/x-icon';
    } else if (extension === 'png') {
      faviconLink.type = 'image/png';
    } else if (extension === 'svg') {
      faviconLink.type = 'image/svg+xml';
    } else if (extension === 'webp') {
      faviconLink.type = 'image/webp';
    }

    document.head.appendChild(faviconLink);
  }
};

// Get branding config from runtime or fallback to defaults
const getBrandingConfig = (): BrandingConfig => {
  // Try to get from runtime config first
  if (typeof window !== 'undefined') {
    // Wait for config to be available (it's loaded asynchronously)
    if ((window as any).BRANDING_CONFIG) {
      const config = (window as any).BRANDING_CONFIG;
      updateDocumentTitle(config);
      updateFavicon(config);
      return config;
    }

    // Create a script tag to load config.js
    const script = document.createElement('script');
    script.src = '/config.js';
    script.onload = () => {
      if ((window as any).BRANDING_CONFIG) {
        const config = (window as any).BRANDING_CONFIG;
        updateDocumentTitle(config);
        updateFavicon(config);
      }
    };
    script.onerror = () => {
      console.error('Failed to load config.js');
    };
    document.head.appendChild(script);
  }

  // Fallback to build-time variables (for development)
  const fallbackConfig = {
    appName: import.meta.env.VITE_UI_APP_NAME || "Cordexa",
    appTitle: import.meta.env.VITE_UI_APP_TITLE || "Ready to work with your data",
    headerTitle: import.meta.env.VITE_UI_HEADER_TITLE || "Cordexa | Your Business Assistant",
    pageTitle: import.meta.env.VITE_UI_PAGE_TITLE || "Cordexa AI",
    logoPath: import.meta.env.VITE_UI_LOGO_PATH || "/assets/cordexa_logo.png",
    faviconPath: import.meta.env.VITE_UI_FAVICON_PATH || "/favicon.png",
    primaryColor: import.meta.env.VITE_UI_PRIMARY_COLOR || "#FFFFFF",
    secondaryColor: import.meta.env.VITE_UI_SECONDARY_COLOR || "#2D87C3",
    accentColor: import.meta.env.VITE_UI_ACCENT_COLOR || "#8DDDD8",
    description: import.meta.env.VITE_UI_DESCRIPTION || "This assistant is a central knowledge base for your organisation",
    contractAssistantTitle: import.meta.env.VITE_UI_CONTRACT_ASSISTANT_TITLE || "Cordexa Contract Analyzer",
    contractAssistantDescription: import.meta.env.VITE_UI_CONTRACT_ASSISTANT_DESCRIPTION || "AI-Powered contract analysis",
    defaultAssistantTitle: import.meta.env.VITE_UI_DEFAULT_ASSISTANT_TITLE || "Cordexa",
    defaultAssistantDescription: import.meta.env.VITE_UI_DEFAULT_ASSISTANT_DESCRIPTION || "This assistant is a central knowledge base for your organisation"
  };
  updateDocumentTitle(fallbackConfig);
  updateFavicon(fallbackConfig);
  return fallbackConfig;
};

const config = getBrandingConfig();

export const branding = {
  appName: config.appName,
  appTitle: config.appTitle,
  headerTitle: config.headerTitle,
  pageTitle: config.pageTitle,
  logoPath: config.logoPath,
  faviconPath: config.faviconPath,
  primaryColor: config.primaryColor,
  secondaryColor: config.secondaryColor,
  accentColor: config.accentColor,
  description: config.description,
  contractAssistantTitle: config.contractAssistantTitle,
  contractAssistantDescription: config.contractAssistantDescription,
  defaultAssistantTitle: config.defaultAssistantTitle,
  defaultAssistantDescription: config.defaultAssistantDescription
};
*/
